/**
 * FailureToAppearFeePanel — what the settings this client missed would cost, and why.
 *
 * The last of the five "no caller" gaps: `GET /manager/ticket/:guid/failure-to-appear-fee` has
 * returned the number, the breakdown and the caveats since Aug 27, and nothing rendered it.
 *
 * **Read-only by design, and it must stay that way.** There is no charge button here and there
 * should never be one. Billing a client is a decision a person takes with the case in front of
 * them; this panel exists so that person has the figure and the reasoning together rather than a
 * number nobody can explain.
 *
 * Every sentence shown is the server's. Court level is derived from the bonds rather than from a
 * court name, whether the newer terms apply comes from the agreement version, and both arrive with
 * a plain-English `reason`. The panel does not compose its own explanations — two accounts of the
 * same money would eventually disagree, and the one on screen would be the one nobody tested.
 *
 * Sits beside BondRecordPanel, which records the two facts this hangs on.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import FailureToAppearFeeApi, {
  COURT_LEVEL_LABELS,
  FailureToAppearFeeResponse,
  MissedSetting,
  formatFeeCents,
} from '../../api/FailureToAppearFeeApi';

interface Props {
  Ticket_guid: string;
}

function formatSettingDate(start: string): string {
  // CourtEvent_Start is epoch milliseconds held as a string.
  const asNumber = Number(start);
  if (!Number.isFinite(asNumber)) return 'Date unknown';
  return DateTime.fromMillis(asNumber).toFormat('d LLL yyyy, h:mm a');
}

const MissedSettingRow: React.FC<{ setting: MissedSetting }> = ({ setting }) => {
  const { assessment } = setting;
  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">{formatSettingDate(setting.CourtEvent_Start)}</Typography>
        {setting.CourtEvent_Desc ? (
          <Typography variant="body2" color="text.secondary">
            {setting.CourtEvent_Desc}
          </Typography>
        ) : null}
        <Chip
          size="small"
          color={assessment.chargeable ? 'warning' : 'default'}
          variant={assessment.chargeable ? 'filled' : 'outlined'}
          label={
            assessment.chargeable
              ? `${formatFeeCents(assessment.amountCents)} chargeable`
              : 'Not chargeable'
          }
        />
      </Stack>

      {setting.note ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Note: {setting.note}
        </Typography>
      ) : null}

      <Typography variant="body2" sx={{ mt: 1 }}>
        {assessment.reason}
      </Typography>

      {assessment.breakdown.length > 0 ? (
        <Box sx={{ mt: 1, pl: 2 }}>
          {assessment.breakdown.map((line) => (
            <Stack key={line.label} direction="row" spacing={2} justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {line.label}
              </Typography>
              <Typography variant="body2">{formatFeeCents(line.amountCents)}</Typography>
            </Stack>
          ))}
        </Box>
      ) : null}

      {/* Never applied automatically: nothing on a calendar row reliably says "jury trial", and
          guessing on money is how a client gets billed $1,500 for a pre-trial conference. */}
      {assessment.chargeable && setting.juryTrialFeeCents ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          If this setting was a jury trial, the fee is {formatFeeCents(setting.juryTrialFeeCents)}{' '}
          instead of the amount above, in any court. Not applied automatically — check the setting
          first.
        </Typography>
      ) : null}

      {assessment.caveats.length > 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          <AlertTitle>Check before charging</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
            {assessment.caveats.map((caveat) => (
              <li key={caveat}>
                <Typography variant="body2">{caveat}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
    </Box>
  );
};

const FailureToAppearFeePanel: React.FC<Props> = ({ Ticket_guid }) => {
  const [data, setData] = useState<FailureToAppearFeeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await FailureToAppearFeeApi.get(Ticket_guid));
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Could not load the assessment.');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) return null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Failure to appear
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the missed settings on this case would cost. Nothing here charges anybody — it is the
        figure and the reasoning, for a person to decide on.
      </Typography>

      {data.feeEnabled === false ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          The failure-to-appear schedule is not switched on. Missed settings are listed, but no
          amount is shown for any of them, and nothing here is chargeable.
        </Alert>
      ) : null}

      <Stack spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Chip
            size="small"
            label={COURT_LEVEL_LABELS[data.courtLevel.level] ?? data.courtLevel.level}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {data.courtLevel.reason}
          </Typography>
        </Box>

        {data.courtLevel.hasUnfiledAppealBond ? (
          <Alert severity="warning">
            An appeal bond is recorded but not filed. The court level — and therefore the fee —
            only moves once it is filed.
          </Alert>
        ) : null}

        <Box>
          <Typography variant="body2" color="text.secondary">
            {data.agreement.reason}
          </Typography>
          {data.agreement.awaitingSignature ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              A newer agreement is marked but unsigned. Somebody has to chase it.
            </Alert>
          ) : null}
        </Box>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {data.missedSettings.length === 0 ? (
        <Alert severity="success">
          No missed settings are recorded on this case, so there is nothing to charge. If the client
          did miss one, record the appearance outcome on the setting first.
        </Alert>
      ) : (
        data.missedSettings.map((setting) => (
          <MissedSettingRow key={setting.CourtEvent_guid} setting={setting} />
        ))
      )}

      {/* Absent while the schedule is switched off. A figure on a screen is a figure somebody
          quotes, so there is nothing to show rather than amounts under a notice saying they do not
          apply. */}
      {data.schedule ? (
        <>
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            The schedule
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Setting</TableCell>
                  <TableCell>Court</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.schedule.map((row) => (
                  <TableRow key={`${row.setting}-${row.court}`}>
                    <TableCell>{row.setting}</TableCell>
                    <TableCell>{row.court}</TableCell>
                    <TableCell align="right">{row.amount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Box>
  );
};

export default FailureToAppearFeePanel;
